
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, PlusCircle, Bookmark } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from '@/lib/supabase';
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

interface CreateDeckFormData {
  name: string;
  description: string;
  category: string;
  subject: string;
}

interface CreateDeckDialogProps {
  onDeckCreated: () => void;
  showFullForm?: boolean;
}

const CreateDeckDialog: React.FC<CreateDeckDialogProps> = ({ onDeckCreated, showFullForm = false }) => {
  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<CreateDeckFormData>({
    defaultValues: {
      name: '',
      description: 'A collection of flashcards for spaced repetition learning',
      category: 'General',
      subject: 'Study'
    }
  });
  const [isOpen, setIsOpen] = useState(false);

  const onSubmit = async (data: CreateDeckFormData) => {
    try {
      const { error } = await supabase
        .from('flashcard_decks')
        .insert([{
          name: data.name,
          description: data.description,
          category: data.category,
          subject: data.subject,
          card_count: 0
        }]);

      if (error) throw error;

      toast.success("Flashcard deck created successfully");
      
      reset();
      setIsOpen(false);
      onDeckCreated();
    } catch (error) {
      console.error("Error creating deck:", error);
      toast.error("Failed to create flashcard deck");
    }
  };

  if (showFullForm) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">Deck Name</Label>
          <Input
            id="name"
            placeholder="Enter deck name"
            {...register("name", { required: "Deck name is required" })}
            className={cn(errors.name && "border-red-500")}
          />
          {errors.name && (
            <p className="text-sm text-red-500">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            placeholder="Enter deck description"
            {...register("description")}
            className="resize-none"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="category">Category</Label>
            <Input
              id="category"
              placeholder="e.g., Mathematics"
              {...register("category", { required: "Category is required" })}
              className={cn(errors.category && "border-red-500")}
            />
            {errors.category && (
              <p className="text-sm text-red-500">{errors.category.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="e.g., Calculus"
              {...register("subject", { required: "Subject is required" })}
              className={cn(errors.subject && "border-red-500")}
            />
            {errors.subject && (
              <p className="text-sm text-red-500">{errors.subject.message}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end">
          <Button 
            type="submit"
            className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>Creating...</>
            ) : (
              <>
                <PlusCircle className="mr-2 h-4 w-4" />
                Create Deck
              </>
            )}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button 
          className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600 text-white"
          size="sm"
        >
          <PlusCircle className="h-4 w-4 mr-2" />
          Create Deck
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bookmark className="h-5 w-5" />
            Create New Flashcard Deck
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Deck Name</Label>
            <Input
              id="name"
              placeholder="Enter deck name"
              {...register("name", { required: "Deck name is required" })}
              className={cn(errors.name && "border-red-500")}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              placeholder="Enter deck description"
              {...register("description")}
              className="resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category</Label>
              <Input
                id="category"
                placeholder="e.g., Mathematics"
                {...register("category", { required: "Category is required" })}
                className={cn(errors.category && "border-red-500")}
              />
              {errors.category && (
                <p className="text-sm text-red-500">{errors.category.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="subject">Subject</Label>
              <Input
                id="subject"
                placeholder="e.g., Calculus"
                {...register("subject", { required: "Subject is required" })}
                className={cn(errors.subject && "border-red-500")}
              />
              {errors.subject && (
                <p className="text-sm text-red-500">{errors.subject.message}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" type="button" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button 
              type="submit"
              className="bg-gradient-to-r from-purple-500 to-blue-500 hover:from-purple-600 hover:to-blue-600"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Creating...' : 'Create Deck'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateDeckDialog;
